import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, getPinUnlockToken, setPinUnlockToken } from '../lib/api';
import { useApp } from '../context/AppContext';
import { useT } from '../lib/i18n';
import { Camera, ImagePlus, FileText, Info, Trash2, ChevronUp, ChevronDown, Loader2, Check, Crop } from 'lucide-react';
import { toast } from 'sonner';
import ImageCropper from '../components/ImageCropper';
import PinGateModal from '../components/PinGateModal';
import UsageChip from '../components/UsageChip';

// Client-side image resize + compression. We aggressively shrink to ~250 KB
// per page (max 1200 px longest edge, JPEG quality 0.72) so a multi-page book
// upload (3-5 pages) stays well under the ~1 MB ingress body limit that was
// silently truncating multi-page saves. If a single page is still oversized
// we drop quality further in one retry, then surface a clear error.
const IMG_MAX_EDGE = 1200;
const IMG_TARGET_KB = 260;

function dataUrlBytes(dataUrl) {
  // "data:image/jpeg;base64,AAAA…" → decoded byte length.
  const idx = dataUrl.indexOf(',');
  if (idx < 0) return dataUrl.length;
  const b64 = dataUrl.slice(idx + 1);
  return Math.floor(b64.length * 3 / 4);
}

async function fileToDataUrl(file, maxSize = IMG_MAX_EDGE) {
  const buf = await file.arrayBuffer();
  const blob = new Blob([buf], { type: file.type });
  const bitmap = await createImageBitmap(blob);
  const scale = Math.min(1, maxSize / Math.max(bitmap.width, bitmap.height));
  const w = Math.round(bitmap.width * scale);
  const h = Math.round(bitmap.height * scale);
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  c.getContext('2d').drawImage(bitmap, 0, 0, w, h);
  let out = c.toDataURL('image/jpeg', 0.72);
  // If a very large / detailed photo is still over target, drop quality once more.
  if (dataUrlBytes(out) > IMG_TARGET_KB * 1024 * 2) {
    out = c.toDataURL('image/jpeg', 0.55);
  }
  return out;
}

export default function AddBook({ appendMode = false }) {
  const nav = useNavigate();
  const { bookId } = useParams();
  const { activeChild, uiLang } = useApp();
  const t = useT(uiLang);
  const [pages, setPages] = useState([]);
  const [title, setTitle] = useState('');
  const [language, setLanguage] = useState(activeChild?.languages?.[0] || 'en');
  // Track whether the parent explicitly picked a language. Once true, we stop
  // auto-switching based on OCR content — parent's choice always wins.
  const [langTouched, setLangTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [languages, setLanguages] = useState([]);
  const [pendingCrop, setPendingCrop] = useState(null); // { src, replaceIndex? }
  // In appendMode we hydrate title + language from the existing book so the
  // parent doesn't have to retype them; save calls /books/{id}/pages instead
  // of creating a new book.
  useEffect(() => {
    if (!appendMode || !bookId) return;
    api.get(`/books/${bookId}`).then(({data}) => {
      setTitle(data.title);
      setLanguage(data.language);
    }).catch(() => {});
  }, [appendMode, bookId]);
  // Defense-in-depth: if this page loads without an active PIN unlock token
  // (e.g. someone navigates here directly by URL), show the gate before any
  // camera/upload UI is exposed.
  const [pinReady, setPinReady] = useState(!!getPinUnlockToken());
  const [pdfBusy, setPdfBusy] = useState(false);
  const [showGuidelines, setShowGuidelines] = useState(false);

  const onPdfUpload = async (file) => {
    if (!file) return;
    // Client-side pre-check for a friendlier error before hitting the server.
    const MB = file.size / (1024 * 1024);
    if (MB > 15) return toast.error(`PDF is ${MB.toFixed(1)} MB — please keep it under 15 MB.`);
    if (!/pdf$/i.test(file.type) && !/\.pdf$/i.test(file.name)) return toast.error('Please upload a PDF file.');
    setPdfBusy(true);
    try {
      const form = new FormData();
      form.append('pdf', file);
      form.append('language', language);
      // NOTE: do NOT set Content-Type manually — axios auto-adds
      // 'multipart/form-data; boundary=…' from FormData. Setting the header
      // ourselves drops the boundary and FastAPI can't parse the body,
      // which surfaces as a misleading "Could not read PDF" 400.
      const { data } = await api.post('/pdf/extract', form);
      // Convert PDF pages into the same shape camera pages use.
      const newPages = (data.pages || []).map(p => ({
        image_base64: p.image_base64 || '',
        text: p.text || '',
        extracting: false,
      }));
      setPages(cur => [...cur, ...newPages]);
      if (!title) setTitle(file.name.replace(/\.pdf$/i, ''));
      toast.success(
        data.is_text_based
          ? `Added ${newPages.length} page${newPages.length !== 1 ? 's' : ''} from PDF (text extracted).`
          : `Added ${newPages.length} page${newPages.length !== 1 ? 's' : ''} from PDF (OCR).`
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not read PDF.');
    } finally {
      setPdfBusy(false);
    }
  };

  useEffect(() => {
    api.get('/config/languages').then(({data}) =>
      setLanguages(data.filter(l => activeChild?.languages?.includes(l.code))));
  }, [activeChild]);

  const runOcrOn = async (b64, insertIndex = null) => {
    const placeholder = { image_base64: b64, text: '', extracting: true };
    setPages(p => {
      if (insertIndex == null) return [...p, placeholder];
      const copy = [...p]; copy[insertIndex] = placeholder; return copy;
    });
    try {
      const { data } = await api.post('/ocr', { image_base64: b64, language });
      // Auto-detect Devanagari — a common source of the "book saved as English
      // but content is Hindi" bug that broke STT. If the parent hasn't picked
      // a language yet AND the OCR text contains Devanagari AND the child has
      // Hindi enabled on their profile, silently switch and inform the parent.
      const looksHindi = /[\u0900-\u097F]/.test(data.text || '');
      if (looksHindi && !langTouched && language !== 'hi' && activeChild?.languages?.includes('hi')) {
        setLanguage('hi');
        toast('Hindi text detected — switched book language to Hindi.');
      }
      setPages(p => p.map(x => x.image_base64 === b64 ? { ...x, text: data.text, extracting: false } : x));
    } catch (e) {
      // Moderation block surfaces as 400 with a friendly detail string.
      // Everything else is a generic OCR error.
      const detail = e?.response?.data?.detail;
      const isBlock = e?.response?.status === 400 && /image can't be uploaded/i.test(String(detail || ''));
      if (isBlock) {
        toast.error(detail);
        // Drop the placeholder — user asked us not to save the flagged image.
        setPages(p => p.filter(x => x.image_base64 !== b64));
      } else {
        toast.error('OCR failed for a page');
        setPages(p => p.map(x => x.image_base64 === b64 ? { ...x, extracting: false } : x));
      }
    }
  };

  const onFile = async (file) => {
    if (!file) return;
    const src = await fileToDataUrl(file);
    setPendingCrop({ src });
  };

  // Gallery multi-select: skip the cropper for batch uploads (parents almost
  // never want to crop 5 photos one by one) and pipe every file straight into
  // OCR. Keeps camera single-shot with crop as the intended manual flow.
  const onGalleryBatch = async (files) => {
    if (!files || files.length === 0) return;
    if (files.length === 1) return onFile(files[0]);
    for (const f of Array.from(files)) {
      const b64 = await fileToDataUrl(f);
      runOcrOn(b64); // fire-and-forget — each runOcrOn appends its own page
    }
  };

  const finishCrop = async (croppedDataUrl) => {
    const p = pendingCrop;
    setPendingCrop(null);
    if (p?.replaceIndex != null) {
      setPages(cur => cur.map((x, i) => i === p.replaceIndex ? { ...x, image_base64: croppedDataUrl, text: '', extracting: true } : x));
      // re-run OCR
      try {
        const { data } = await api.post('/ocr', { image_base64: croppedDataUrl, language });
        setPages(cur => cur.map((x, i) => i === p.replaceIndex ? { ...x, text: data.text, extracting: false } : x));
      } catch (e) {
        const detail = e?.response?.data?.detail;
        const isBlock = e?.response?.status === 400 && /image can't be uploaded/i.test(String(detail || ''));
        toast.error(isBlock ? detail : 'OCR failed');
        setPages(cur => cur.map((x, i) => i === p.replaceIndex ? { ...x, extracting: false } : x));
      }
    } else {
      await runOcrOn(croppedDataUrl);
    }
  };

  const move = (idx, dir) => {
    setPages(p => {
      const copy = [...p];
      const j = idx + dir;
      if (j < 0 || j >= copy.length) return copy;
      [copy[idx], copy[j]] = [copy[j], copy[idx]];
      return copy;
    });
  };
  const remove = (idx) => setPages(p => p.filter((_, i) => i !== idx));
  const editText = (idx, text) => setPages(p => p.map((x, i) => i === idx ? { ...x, text } : x));
  const recrop = (idx) => setPendingCrop({ src: pages[idx].image_base64, replaceIndex: idx });

  const save = async () => {
    if (!title || pages.length === 0) return toast.error('Add a title and at least one page');
    if (pages.some(p => p.extracting)) return toast.error('Wait for text extraction to finish');
    setSaving(true);
    try {
      const pagesPayload = pages.map(p => ({ image_base64: p.image_base64, text: p.text }));
      if (appendMode) {
        const { data } = await api.post(`/books/${bookId}/pages`, { pages: pagesPayload });
        toast.success(`Added ${data.added} page${data.added !== 1 ? 's' : ''} — book now has ${data.total_pages} pages.`);
        nav(`/book/${bookId}`);
      } else {
        const { data } = await api.post('/books', {
          child_id: activeChild.child_id, title, language, pages: pagesPayload,
        });
        toast.success(`Book saved with ${pages.length} page${pages.length !== 1 ? 's' : ''}!`);
        nav(`/read/${data.book_id}`);
      }
    } catch (e) {
      // Never fail silently — surface the actual reason so parents know what to do.
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      // Token expired or lost between page load and save — reopen the gate
      // so the parent can re-enter their PIN instead of hitting a dead end.
      if (status === 403 && /pin/i.test(String(detail || ''))) {
        setPinUnlockToken(null);
        setPinReady(false);
        toast('Please re-enter your parent/guardian PIN to save.');
      } else if (status === 413 || /request entity too large/i.test(String(detail))) {
        toast.error(`Photos are too big — try again with fewer pages, or use the Upload PDF option instead.`);
      } else if (!status) {
        toast.error('Network error while saving — check your connection and try again.');
      } else {
        toast.error(`Could not save book: ${detail || `HTTP ${status}`}`);
      }
    } finally { setSaving(false); }
  };

  // Surface OCR failures too so a silently-empty page never sneaks into a book.
  const _failedPages = pages.filter(p => !p.extracting && !p.text).length; // eslint-disable-line no-unused-vars

  return (
    <div className="px-6 pt-6 pb-36 max-w-3xl mx-auto overflow-x-hidden">
      {!pinReady && (
        <PinGateModal
          onUnlocked={() => setPinReady(true)}
          onCancel={() => nav(-1)}
        />
      )}
      <h1 className="text-3xl font-extrabold mb-6">{t('capturePages')}</h1>

      {/* Iter-51.58 — plan usage chip: pages used + uploads left this
          month. Shown before any capture control so parents see their
          remaining budget upfront, not after they hit a 402. */}
      <div className="mb-4">
        <UsageChip variant="inline" dataTestId="addbook-usage-chip"/>
      </div>

      <div className="bg-white rounded-3xl p-6 mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <div>
            <label className="text-xs font-bold uppercase text-slate-500">{t('bookTitle')}</label>
            <input data-testid="book-title-input" value={title} onChange={e => setTitle(e.target.value)}
              spellCheck={false} autoCorrect="off" autoCapitalize="off"
              className="w-full mt-1 px-4 py-3 rounded-2xl border-2 border-slate-200 font-bold"/>
          </div>
          <div>
            <label className="text-xs font-bold uppercase text-slate-500">{t('languages')} <span className="text-[#E8435C]">*</span></label>
            <select data-testid="book-language-select" value={language}
              onChange={e => { setLanguage(e.target.value); setLangTouched(true); }}
              className="w-full mt-1 px-4 py-3 rounded-2xl border-2 border-slate-200 font-bold">
              {languages.map(l => <option key={l.code} value={l.code}>{l.name}</option>)}
            </select>
            {(() => {
              // Warn when the parent's chosen language visibly disagrees with
              // the OCR content — usually means the book was saved as English
              // but the pages are Hindi. Left as a soft warning, not a block,
              // because bilingual pages are a legitimate case.
              const anyText = pages.filter(p => p.text).map(p => p.text).join(' ');
              if (!anyText) return null;
              const hasHi = /[\u0900-\u097F]/.test(anyText);
              const hasEn = /[A-Za-z]{4,}/.test(anyText);
              if (language === 'en' && hasHi && !hasEn) {
                return <p data-testid="lang-mismatch-warning" className="mt-1 text-xs font-semibold text-[#E8435C]">Pages look Hindi — please switch language to Hindi.</p>;
              }
              if (language === 'hi' && hasEn && !hasHi) {
                return <p data-testid="lang-mismatch-warning" className="mt-1 text-xs font-semibold text-[#E8435C]">Pages look English — please switch language to English.</p>;
              }
              return null;
            })()}
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <label data-testid="camera-photo-btn" className="pushable pushable-primary bg-[#FF6B1A] text-white font-bold px-6 py-4 rounded-full inline-flex items-center gap-2 cursor-pointer">
            <Camera size={22} strokeWidth={3}/> {t('fromCamera')}
            <input type="file" accept="image/*" capture="environment" onChange={e => onFile(e.target.files?.[0])} className="hidden"/>
          </label>
          <label data-testid="gallery-photo-btn" className="pushable bg-white border-2 border-slate-200 text-slate-700 font-bold px-6 py-4 rounded-full inline-flex items-center gap-2 cursor-pointer">
            <ImagePlus size={22} strokeWidth={3}/> {t('fromGallery')}
            <input type="file" accept="image/*" multiple onChange={e => { onGalleryBatch(e.target.files); e.target.value=''; }} className="hidden"/>
          </label>
          <label data-testid="pdf-upload-btn" className={`pushable bg-[#5B8DEF] text-white font-bold px-6 py-4 rounded-full inline-flex items-center gap-2 cursor-pointer ${pdfBusy ? 'opacity-60 pointer-events-none' : ''}`}>
            {pdfBusy ? <Loader2 size={22} className="animate-spin"/> : <FileText size={22} strokeWidth={3}/>}
            {pdfBusy ? 'Reading PDF…' : 'Upload PDF'}
            <input type="file" accept="application/pdf,.pdf" onChange={e => { const f = e.target.files?.[0]; e.target.value=''; onPdfUpload(f); }} className="hidden"/>
          </label>
        </div>

        <div className="mt-3 flex items-start gap-2 text-xs text-slate-500">
          <button data-testid="pdf-guidelines-toggle" type="button" onClick={() => setShowGuidelines(v => !v)} className="inline-flex items-center gap-1 font-bold text-[#5B8DEF] hover:underline">
            <Info size={14} strokeWidth={2.8}/> Guidelines
          </button>
          <span>Printed/typed PDFs only • max 15 MB.</span>
        </div>
        {showGuidelines && (
          <div data-testid="pdf-guidelines-panel" className="mt-3 bg-[#EAF4FB] border border-[#5B8DEF]/30 rounded-2xl p-4 text-sm text-[#2B2140] space-y-2">
            <p className="font-extrabold">Upload guidelines</p>
            <p><b>Good to upload:</b> printed worksheets, question papers, exercise sheets (from teachers, downloads, or AI-generated content).</p>
            <p><b>Not supported:</b> handwritten pages — OCR is unreliable on handwriting and could produce inaccurate reading content. Please upload printed / typed PDFs only.</p>
            <p><b>Limits:</b> 15 MB per PDF. Total active pages across your library are capped by your plan (Starter allows 20). This is meant for supplementary material, not full textbooks.</p>
          </div>
        )}
      </div>

      {pages.length > 0 && (
        <>
          <h2 className="text-2xl font-extrabold mb-3">{t('reviewPages')}</h2>
          <p className="text-sm text-slate-500 mb-4">{t('reorder')}</p>
          <div className="space-y-3 mb-6">
            {pages.map((p, i) => (
              <div data-testid={`page-item-${i}`} key={i} className="bg-white rounded-3xl p-4 flex gap-4">
                <div className="flex flex-col gap-2 items-center">
                  <img src={p.image_base64} alt="" className="w-24 h-32 rounded-2xl object-cover border-2 border-slate-100"/>
                  <button data-testid={`page-recrop-${i}`} onClick={() => recrop(i)} className="text-xs font-bold text-[#FF6B1A] inline-flex items-center gap-1"><Crop size={12}/> {t('cropAdjust')}</button>
                </div>
                <div className="flex-1">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs font-bold uppercase text-slate-500">Page {i+1}</span>
                    <div className="flex gap-1">
                      <button data-testid={`page-up-${i}`} onClick={() => move(i, -1)} className="p-2 rounded-full bg-slate-100"><ChevronUp size={16}/></button>
                      <button data-testid={`page-down-${i}`} onClick={() => move(i, 1)} className="p-2 rounded-full bg-slate-100"><ChevronDown size={16}/></button>
                      <button data-testid={`page-del-${i}`} onClick={() => remove(i)} className="p-2 rounded-full bg-red-100 text-red-600"><Trash2 size={16}/></button>
                    </div>
                  </div>
                  {p.extracting ? (
                    <div className="flex items-center gap-2 text-slate-500 py-4"><Loader2 size={16} className="animate-spin"/> {t('extractingText')}</div>
                  ) : (
                    <textarea data-testid={`page-text-${i}`} value={p.text} onChange={e => editText(i, e.target.value)} rows={5}
                      spellCheck={false} autoCorrect="off" autoCapitalize="off" autoComplete="off"
                      className={`w-full rounded-2xl border-2 border-slate-200 p-3 font-bold text-lg whitespace-pre-wrap ${language==='hi'?'font-hi':''}`}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="flex gap-3">
            <button data-testid="cancel-book-btn" onClick={() => nav('/books')} className="pushable bg-white border-2 border-slate-200 flex-1 px-6 py-4 rounded-full font-bold">{t('cancel')}</button>
            <button data-testid="save-book-btn" disabled={saving} onClick={save} className="pushable pushable-primary bg-[#FF6B1A] text-white flex-1 px-6 py-4 rounded-full font-bold inline-flex items-center justify-center gap-2">
              {saving ? <Loader2 className="animate-spin" size={20}/> : <Check size={20} strokeWidth={3}/>} {t('saveBook')}
            </button>
          </div>
        </>
      )}

      {pendingCrop && (
        <ImageCropper src={pendingCrop.src} onCancel={() => setPendingCrop(null)} onCropped={finishCrop}/>
      )}
    </div>
  );
}
